import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faX } from '@fortawesome/free-solid-svg-icons';
import { isEmpty } from 'lodash';
import { withAppHOC } from '../../hoc';
import {
  StyledContentContainer,
  StyledFiltersContainer,
  StyledFilters,
  StyledFilterContainer,
  StyledSearchButton,
  StyledResetButton,
  StyledIconedText,
} from '../../components/styled';
import PageHeader from '../../components/PageHeader/PageHeader';
import { MessageBox, Dropdown } from '../../components/FormElements';
import Loader from '../../components/Loader/Loader';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import NoData from '../../components/NoData/NoData';
import { TableBuilder } from '../../components/TableElements';
import Pagination from '../../components/Pagination/Pagination';
import API from '../../api';
import { RESPONSE_STATUSES } from '../../constants';
import { formatCurrency, getSearchParams } from '../../utils';
import FiltersButton from '../../components/FiltersButton/FiltersButton';
import { Image } from '../../components/Image';

const AvailableMarginsList = () => {
  const [showFilters, setShowFilters] = useState(false);
  const onClickShowFilters = () => {
    setShowFilters((showFilters) => !showFilters);
  };
  const [filters, setFilters] = useState({
    fOperatorsId: '',
    fCirclesId: '',
    fCategoriesId: '',
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams('pageNumber') || 1,
    recordsPerPage: getSearchParams('recordsPerPage') || 30,
  });

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const readMeta = async () => {
    try {
      const response = await API.get(`/meta`);
      return response?.data?.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const readData = async () => {
    try {
      const response = await API.get(
        `/buyer/available-margins?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&fOperatorsId=${appliedFilters.fOperatorsId}&fCirclesId=${appliedFilters.fCirclesId}&fCategoriesId=${appliedFilters.fCategoriesId}`
      );
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const { data: meta } = useQuery({
    queryKey: ['meta'],
    queryFn: readMeta,
  });

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ['available_margins', paginationData, appliedFilters],
    queryFn: readData,
  });

  const reloadData = async () => await refetch();

  const filterData = async (e) => {
    e.preventDefault();
    setAppliedFilters(filters);
  };

  const clearFilters = async (e) => {
    e.preventDefault();
    setFilters({
      fOperatorsId: '',
      fCirclesId: '',
      fCategoriesId: '',
    });
    setAppliedFilters({
      fOperatorsId: '',
      fCirclesId: '',
      fCategoriesId: '',
    });
  };

  // const blockOperator = (sellersId, operatorsId) => {
  //   API.post('/buyer/available-margins-operator-blocker', {
  //     sellersId,
  //     operatorsId,
  //   }).then((response) => {
  //     const { status } = response.data;
  //     if (status === RESPONSE_STATUSES.SUCCESS) {
  //       window.location.reload();
  //     }
  //   });
  // };

  // const unblockOperator = (sellersId, operatorsId) => {
  //   API.put(`/buyer/available-margins-operator-blocker`, {
  //     sellersId,
  //     operatorsId,
  //   }).then((response) => {
  //     const { status } = response.data;
  //     if (status === RESPONSE_STATUSES.SUCCESS) {
  //       window.location.reload();
  //     }
  //   });
  // };

  // const blockSeller = (sellersId) => {
  //   API.post('/buyer/available-margins-seller-blocker', {
  //     sellersId,
  //   }).then((response) => {
  //     const { status } = response.data;
  //     if (status === RESPONSE_STATUSES.SUCCESS) {
  //       window.location.reload();
  //     }
  //   });
  // };

  // const unblockSeller = (sellersId) => {
  //   API.put(`/buyer/available-margins-seller-blocker`, {
  //     sellersId,
  //   }).then((response) => {
  //     const { status } = response.data;
  //     if (status === RESPONSE_STATUSES.SUCCESS) {
  //       window.location.reload();
  //     }
  //   });
  // };

  const renderPageHeading = () => (
    <PageHeader
      title="Available Margins"
      showAddButton={false}
      showReloadData={true}
      reloadData={reloadData}
    />
  );

  const operatorsOptions = [
    {
      name: '-- SELECT OPERATOR --',
      value: '',
    },
  ];
  !isEmpty(meta?.operators) &&
    meta?.operators?.forEach(({ operatorName, id }) =>
      operatorsOptions?.push({ name: operatorName, value: id })
    );

  const circlesOptions = [
    {
      name: '-- SELECT CIRCLE --',
      value: '',
    },
  ];
  !isEmpty(meta?.circles) &&
    meta?.circles?.forEach(({ circleName, id }) =>
      circlesOptions?.push({ name: circleName, value: id })
    );

  const categoriesOptions = [
    {
      name: '-- SELECT CATEGORY --',
      value: '',
    },
  ];
  !isEmpty(meta?.categories) &&
    meta?.categories?.forEach(({ category, id }) =>
      categoriesOptions?.push({ name: category, value: id })
    );

  if (isLoading) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <LoadingSpinner />
        </StyledContentContainer>
      </>
    );
  }

  if (error) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={error.message}
          />
        </StyledContentContainer>
      </>
    );
  }

  return (
    <>
      <Loader isLoading={isRefetching} />
      {renderPageHeading()}
      <FiltersButton onClick={onClickShowFilters} value={showFilters} />
      <StyledContentContainer graybackground="true">
        <StyledFiltersContainer className={showFilters ? 'show' : ''}>
          <StyledFilters>
            <StyledFilterContainer>
              <Dropdown
                value={filters.fOperatorsId}
                onChange={(value) => handleFilterChange('fOperatorsId', value)}
                placeholder="Operator"
                disabled={isLoading}
                options={operatorsOptions}
              />
              <Dropdown
                value={filters.fCirclesId}
                onChange={(value) => handleFilterChange('fCirclesId', value)}
                placeholder="Circle"
                disabled={isLoading}
                options={circlesOptions}
              />
              <Dropdown
                value={filters.fCategoriesId}
                onChange={(value) => handleFilterChange('fCategoriesId', value)}
                placeholder="Category"
                disabled={isLoading}
                options={categoriesOptions}
              />
            </StyledFilterContainer>
            <StyledSearchButton onClick={filterData} type="button">
              <FontAwesomeIcon icon={faSearch} />
              Search
            </StyledSearchButton>
            <StyledResetButton onClick={clearFilters} type="button">
              <FontAwesomeIcon icon={faX} />
              Clear
            </StyledResetButton>
          </StyledFilters>
        </StyledFiltersContainer>
        {data?.status === RESPONSE_STATUSES.FAILED && (
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={data?.message}
          />
        )}
        {data?.data?.length === 0 ? (
          <NoData />
        ) : (
          <>
            <TableBuilder
              isLoading={isLoading}
              tableHeadings={
                false
                  ? [
                      // {
                      //   title: 'Sellers ID',
                      //   dataSelector: 'sellersId',
                      //   dataType: 'string',
                      // },
                      {
                        title: 'Operator',
                        dataSelector: 'operatorName',
                        dataType: 'string',
                        cellrenderer: (value, item) => (
                          <StyledIconedText>
                            <Image
                              source={item.operatorsLogo || '/no-image.png'}
                              alt={value}
                              style={{ width: '50px', height: '50px' }}
                            />
                            {value}
                          </StyledIconedText>
                        ),
                      },
                      {
                        title: 'Circle',
                        dataSelector: 'circleName',
                        dataType: 'string',
                      },
                      {
                        title: 'Category',
                        dataSelector: 'category',
                        dataType: 'string',
                      },
                      {
                        title: 'Margin Type',
                        dataSelector: 'marginType',
                        dataType: 'string',
                      },
                      {
                        title: 'GST Type',
                        dataSelector: 'gstType',
                        dataType: 'string',
                        cellrenderer: (value, data) =>
                          data?.marginType === 'GST' && value ? value : 'N/A',
                      },
                      {
                        title: 'Amount',
                        dataSelector: 'amount',
                        dataType: 'number',
                      },
                      {
                        title: 'Margin',
                        dataSelector: 'processedMargin',
                        dataType: 'string',
                        cellrenderer: (value) => `${value}%`,
                      },
                      {
                        title: 'Available Limit',
                        dataSelector: 'availableLimit',
                        dataType: 'number',
                        cellrenderer: (value) => formatCurrency(value),
                      },
                      // {
                      //   title: 'Refunds',
                      //   dataSelector: 'refundedPercentage',
                      //   dataType: 'string',
                      //   cellrenderer: (value) => (value ? `${value}%` : 'N/A'),
                      // },
                      // {
                      //   title: 'Callback',
                      //   dataSelector: 'callbackSuccessRate',
                      //   dataType: 'string',
                      //   cellrenderer: (value) => (value ? `${value}%` : 'N/A'),
                      // },
                      // {
                      //   title: 'Operator Status',
                      //   dataSelector: 'operatorStatus',
                      //   dataType: 'string',
                      //   canSort: false,
                      //   textAlign: 'center',
                      //   cellrenderer: (value, data) => (
                      //     <Switch
                      //       value={value === 'Active'}
                      //       onChange={() =>
                      //         value === 'Active'
                      //           ? blockOperator(
                      //               data.sellersId,
                      //               data.operatorsId
                      //             )
                      //           : unblockOperator(
                      //               data.sellersId,
                      //               data.operatorsId
                      //             )
                      //       }
                      //     />
                      //   ),
                      // },
                      // {
                      //   title: 'Seller Status',
                      //   dataSelector: 'sellerStatus',
                      //   dataType: 'string',
                      //   canSort: false,
                      //   textAlign: 'center',
                      //   cellrenderer: (value, data) => (
                      //     <Switch
                      //       value={value === 'Active'}
                      //       onChange={() =>
                      //         value === 'Active'
                      //           ? blockSeller(data.sellersId)
                      //           : unblockSeller(data.sellersId)
                      //       }
                      //     />
                      //   ),
                      // },
                      {
                        title: 'LAPU/API',
                        dataSelector: 'isLapu',
                        dataType: 'string',
                        canSort: false,
                        textAlign: 'center',
                        cellrenderer: (value) => value ?? 'N/A',
                      },
                    ]
                  : [
                      // {
                      //   title: 'Sellers ID',
                      //   dataSelector: 'sellersId',
                      //   dataType: 'string',
                      // },
                      {
                        title: 'Operator',
                        dataSelector: 'operatorName',
                        dataType: 'string',
                        cellrenderer: (value, item) => (
                          <StyledIconedText>
                            <Image
                              source={item.operatorsLogo || '/no-image.png'}
                              alt={value}
                              style={{ width: '50px', height: '50px' }}
                            />
                            {value}
                          </StyledIconedText>
                        ),
                      },
                      {
                        title: 'Circle',
                        dataSelector: 'circleName',
                        dataType: 'string',
                      },
                      {
                        title: 'Category',
                        dataSelector: 'category',
                        dataType: 'string',
                      },
                      {
                        title: 'Margin Type',
                        dataSelector: 'marginType',
                        dataType: 'string',
                      },
                      {
                        title: 'GST Type',
                        dataSelector: 'gstType',
                        dataType: 'string',
                        cellrenderer: (value, data) =>
                          data?.marginType === 'GST' && value ? value : 'N/A',
                      },
                      {
                        title: 'Amount',
                        dataSelector: 'amount',
                        dataType: 'number',
                      },
                      {
                        title: 'Margin',
                        dataSelector: 'processedMargin',
                        dataType: 'string',
                        cellrenderer: (value) => `${value}%`,
                      },
                      {
                        title: 'Available Limit',
                        dataSelector: 'availableLimit',
                        dataType: 'number',
                        cellrenderer: (value) => formatCurrency(value),
                      },
                      // {
                      //   title: 'Refunds',
                      //   dataSelector: 'refundedPercentage',
                      //   dataType: 'string',
                      //   cellrenderer: (value) => (value ? `${value}%` : 'N/A'),
                      // },
                      // {
                      //   title: 'Callback',
                      //   dataSelector: 'callbackSuccessRate',
                      //   dataType: 'string',
                      //   cellrenderer: (value) => (value ? `${value}%` : 'N/A'),
                      // },
                      // {
                      //   title: 'Operator Status',
                      //   dataSelector: 'operatorStatus',
                      //   dataType: 'string',
                      //   canSort: false,
                      //   textAlign: 'center',
                      //   cellrenderer: (value, data) => (
                      //     <Switch
                      //       value={value === 'Active'}
                      //       onChange={() =>
                      //         value === 'Active'
                      //           ? blockOperator(
                      //               data.sellersId,
                      //               data.operatorsId
                      //             )
                      //           : unblockOperator(
                      //               data.sellersId,
                      //               data.operatorsId
                      //             )
                      //       }
                      //     />
                      //   ),
                      // },
                      // {
                      //   title: 'Seller Status',
                      //   dataSelector: 'sellerStatus',
                      //   dataType: 'string',
                      //   canSort: false,
                      //   textAlign: 'center',
                      //   cellrenderer: (value, data) => (
                      //     <Switch
                      //       value={value === 'Active'}
                      //       onChange={() =>
                      //         value === 'Active'
                      //           ? blockSeller(data.sellersId)
                      //           : unblockSeller(data.sellersId)
                      //       }
                      //     />
                      //   ),
                      // },
                    ]
              }
              tableData={data?.data}
            />
            <Pagination
              totalRecords={data?.totalRecords}
              paginationData={paginationData}
              setPaginationData={setPaginationData}
            />
          </>
        )}
      </StyledContentContainer>
    </>
  );
};

export default withAppHOC(AvailableMarginsList);
